import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Cpu, Thermometer, Zap, Power, ArrowDownToLine, Fan, AlertTriangle, TrendingUp, Wallet, Crosshair, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* CIVCOIN RIG TERMINAL — in-game crypto mining (CEF) — /cef/crypto/:serverId?token=<session>.
   Opened when a player walks up to their rig. Mining accrues server-side from timestamps
   (thermal throttle model); COLLECT sells the CivCoin buffer at the server's drifting
   market rate straight into the bank. Demo floor without a token. */

const CYAN = "#22d3ee";
const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

export default function CryptoCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  /* transparent CEF chrome — the game world stays visible behind the terminal rail */
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);
  const sfx = usePhoneAudio();

  const [rig, setRig] = useState(demo ? { running: false, hashrate_mh: 0, temp_c: 34, watts: 12, throttle: 1, pending_civ: 0, total_mined_civ: 1.2841, shutdown: false } : null);
  const [rate, setRate] = useState(demo ? 24.5 : null);
  const [rateHist, setRateHist] = useState([24.5]);
  const [bank, setBank] = useState(demo ? 4820 : null);
  const [busy, setBusy] = useState(false);
  const [demoStart, setDemoStart] = useState(null);
  const [shop, setShop] = useState(demo ? {
    gpu: { level: 0, maxed: false, next_price: 2500, effect: "+18 MH/s hashrate (+43% CIV yield)", label: "GPU array" },
    cooling: { level: 0, maxed: false, next_price: 1200, effect: "slower heat, later thermal throttle", label: "Cooling loop" },
  } : null);
  const pendingRef = useRef(0);
  const [, force] = useState(0);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/crypto/status`, { params: { token } })
      .then((r) => {
        setRig(r.data.rig); setRate(r.data.rate); setBank(r.data.bank);
        pendingRef.current = r.data.rig.pending_civ;
        setRateHist((h) => [...h, r.data.rate].slice(-30));
        setAlerts(r.data.alerts || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/crypto/rivals`, { params: { token } })
      .then((r) => setRivals(r.data.rivals))
      .catch(() => {});
    api.get(`/nc/servers/${serverId}/crypto/shop`, { params: { token } })
      .then((r) => setShop(r.data.parts))
      .catch(() => {});
  }, [demo, serverId, token]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  // smooth client-side accrual between polls (demo + live)
  useEffect(() => {
    const t = setInterval(() => {
      const running = demo ? !!demoStart : rig?.running;
      if (!running) return;
      const throttle = rig?.throttle ?? 1;
      pendingRef.current += (12 * throttle) / 3600; // per second
      force((x) => x + 1);
      if (demo && rig) {
        const mins = (Date.now() - demoStart) / 60000;
        const temp = Math.min(84, 34 + mins * 0.9);
        const th = temp < 70 ? 1 : temp < 78 ? 0.6 : 0.35;
        setRig((r) => ({ ...r, temp_c: Math.round(temp * 10) / 10, throttle: th, hashrate_mh: Math.round(42 * th * 10) / 10 }));
      }
    }, 1000);
    return () => clearInterval(t);
  }, [demo, demoStart, rig]);

  const buyUpgrade = async (part) => {
    sfx.keyTick();
    const spec = shop?.[part];
    if (!spec || spec.maxed) return;
    if (demo) {
      if (bank < spec.next_price) { toast.error("Not enough in the bank for that tier"); return; }
      const prices = part === "gpu" ? [2500, 8000, 20000] : [1200, 4000, 12000];
      const lvl = spec.level + 1;
      setBank((b) => b - spec.next_price);
      setShop((s) => ({ ...s, [part]: { ...s[part], level: lvl, next_price: prices[lvl] ?? null, maxed: lvl >= 3 } }));
      setRig((r) => ({ ...r, hashrate_mh: r.running ? (42 + 18 * (part === "gpu" ? lvl : shop.gpu.level)) * (r.throttle ?? 1) : r.hashrate_mh }));
      toast.success(`${spec.label} tier ${lvl} installed`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/crypto/upgrade`, { part }, { params: { token } });
      toast.success(r.data.message);
      setBank(r.data.bank);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const act = async (what) => {
    sfx.keyTick();
    if (demo) {
      if (what === "start") { setDemoStart(Date.now()); setRig((r) => ({ ...r, running: true, hashrate_mh: 42, watts: 850 })); toast.success("Rig online — hashing CivCoin at 42 MH/s"); }
      if (what === "stop") { setDemoStart(null); setRig((r) => ({ ...r, running: false, hashrate_mh: 0, watts: 12, pending_civ: pendingRef.current })); toast.success(`Rig idling — ${pendingRef.current.toFixed(4)} CIV in the buffer`); }
      if (what === "collect") {
        if (pendingRef.current < 0.01) { toast.error("Nothing worth selling yet — let the rig run"); return; }
        const usd = Math.round(pendingRef.current * rate);
        setBank((b) => b + usd);
        toast.success(`Sold ${pendingRef.current.toFixed(4)} CIV @ $${rate} — ${fmt$(usd)} wired to your bank`);
        pendingRef.current = 0;
        setRig((r) => ({ ...r, total_mined_civ: r.total_mined_civ }));
      }
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/crypto/${what}`, {}, { params: { token } });
      toast.success(r.data.message);
      if (r.data.bank != null) setBank(r.data.bank);
      if (r.data.rig) { setRig(r.data.rig); pendingRef.current = r.data.rig.pending_civ; }
      if (what === "collect") pendingRef.current = 0;
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  /* ---- rival rigs (sabotage) ---- */
  const [rivals, setRivals] = useState(demo ? [
    { playercode: 88112, name: "Kira 'Watt' Voss", gpu_level: 2, hashrate_mh: 74.4, pending_civ: 3.2041, est_value: 78.5 },
    { playercode: 88113, name: "Moss Delgado", gpu_level: 0, hashrate_mh: 42.0, pending_civ: 0.9123, est_value: 22.35 },
  ] : []);
  const [alerts, setAlerts] = useState([]);
  const [sabBusy, setSabBusy] = useState(0);

  const sabotage = async (r) => {
    if (sabBusy) return;
    sfx.keyTick();
    setSabBusy(r.playercode);
    if (demo) {
      setTimeout(() => {
        if (Math.random() < 0.7) {
          const cutCiv = r.pending_civ * 0.4;
          const usd = Math.max(1, Math.round(cutCiv * (rate || 24.5)));
          setBank((b) => b + usd);
          setRivals((rs) => rs.filter((x) => x.playercode !== r.playercode));
          sfx.success();
          toast.success(`Short-circuited ${r.name}'s rig — skimmed ${cutCiv.toFixed(4)} CIV, fenced for ${fmt$(usd)}`);
        } else {
          setBank((b) => Math.max(0, b - 500));
          toast.error("Botched — the breaker tripped and security fined you $500");
        }
        setSabBusy(0);
      }, 900);
      return;
    }
    try {
      const res = await api.post(`/nc/servers/${serverId}/crypto/sabotage`, { target_playercode: r.playercode }, { params: { token } });
      if (res.data.success) { sfx.success(); toast.success(res.data.message); setRivals((rs) => rs.filter((x) => x.playercode !== r.playercode)); }
      else toast.error(res.data.message);
      if (res.data.bank != null) setBank(res.data.bank);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSabBusy(0); }
  };

  const pending = pendingRef.current;
  const est = pending * (rate || 0);
  const temp = rig?.temp_c ?? 34;
  const tempColor = temp < 70 ? "#34d399" : temp < 78 ? "#fbbf24" : "#ef4444";
  const maxHist = Math.max(...rateHist, 1);
  const minHist = Math.min(...rateHist, 0);

  return (
    <div data-testid="crypto-page" className="fixed inset-0 overflow-hidden text-zinc-100 pointer-events-none" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="mining terminal" />}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" aria-hidden />
      {/* right-hand terminal rail — the rest of the screen stays the game world */}
      <div className="absolute inset-y-0 right-0 pointer-events-auto w-[min(600px,96vw)] flex flex-col border-l border-[#22d3ee33]"
        style={{ background: "linear-gradient(200deg, rgba(4,18,26,0.93), rgba(2,7,11,0.95))", backdropFilter: "blur(16px)", boxShadow: "-30px 0 80px #00000088" }}>

      <header className="relative z-10 flex items-center gap-3 px-5 py-3.5 border-b border-[#22d3ee22] flex-wrap">
        <Cpu className="w-6 h-6" style={{ color: CYAN }} />
        <div>
          <h1 className="font-black uppercase tracking-[0.3em] text-lg" style={{ color: CYAN }}>RigLink OS</h1>
          <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.25em]">CivCoin (CIV) mining terminal · rig #1</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div data-testid="crypto-bank" className="flex items-center gap-2 border border-white/15 bg-black/60 px-4 py-2">
            <Wallet className="w-4 h-4 text-zinc-400" /><span className="font-mono text-sm font-bold">{bank == null ? "…" : fmt$(bank)}</span>
          </div>
          <div data-testid="crypto-rate" className="flex items-center gap-2 border px-4 py-2" style={{ borderColor: `${CYAN}44`, backgroundColor: "#00000088" }}>
            <TrendingUp className="w-4 h-4" style={{ color: CYAN }} />
            <span className="font-mono text-sm font-bold" style={{ color: CYAN }}>${rate == null ? "…" : rate.toFixed(2)}</span>
            <span className="font-mono text-[8px] text-zinc-500 uppercase">/ CIV</span>
          </div>
        </div>
      </header>

      <div className="relative z-10 grid grid-cols-1 gap-4 p-5 flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        {/* GPU rack */}
        <div className="lg:col-span-2 border border-white/10 bg-black/50 p-5 flex flex-col">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Rig rack — 3× GPU</p>
          <div className="flex flex-col gap-3 flex-1 justify-center">
            {[0, 1, 2].map((i) => (
              <div key={i} data-testid={`gpu-${i}`} className="border border-white/10 bg-[#0a0f14] px-4 py-3 flex items-center gap-4">
                <div className="relative w-10 h-10">
                  <Fan className={`w-10 h-10 ${rig?.running ? "animate-spin" : "text-zinc-600"}`} style={rig?.running ? { color: CYAN, animationDuration: "0.9s" } : {}} />
                </div>
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300">GPU-{i} · NC-3080 Civic</p>
                  <p className="font-mono text-[8px] text-zinc-500">{rig?.running ? `${Math.round((rig?.hashrate_mh || 0) / 3 * 10) / 10} MH/s · drawing power` : "standby"}</p>
                </div>
                <span className={`w-2 h-2 rounded-full ${rig?.running ? "animate-pulse" : ""}`} style={{ backgroundColor: rig?.running ? CYAN : "#3f3f46", boxShadow: rig?.running ? `0 0 8px ${CYAN}` : "none" }} />
              </div>
            ))}
          </div>
          <button data-testid="crypto-toggle" disabled={busy} onClick={() => act(rig?.running ? "stop" : "start")}
            className={`mt-5 w-full py-4 font-mono text-xs uppercase tracking-[0.35em] font-bold flex items-center justify-center gap-3 transition-all ${rig?.running ? "border border-red-500/50 text-red-400 hover:bg-red-500/10" : "text-black"}`}
            style={rig?.running ? {} : { backgroundColor: CYAN, boxShadow: `0 0 30px ${CYAN}44` }}>
            <Power className="w-4 h-4" /> {rig?.running ? "Stop mining" : "Start mining"}
          </button>

          {/* rig upgrades — paid from the bank */}
          {shop && (
            <div data-testid="crypto-shop" className="mt-4 border-t border-white/10 pt-4 flex flex-col gap-2.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Rig upgrades — paid from bank</p>
              {["gpu", "cooling"].map((part) => {
                const spec = shop[part];
                return (
                  <div key={part} className="border border-white/10 bg-black/40 px-3.5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-200">{spec.label}</p>
                      <p className="font-mono text-[8px] text-zinc-500 mt-0.5">{spec.maxed ? "MAX TIER" : spec.effect}</p>
                      <div className="flex gap-1 mt-1.5">
                        {[1, 2, 3].map((lv) => (
                          <span key={lv} className="w-4 h-1.5" style={{ backgroundColor: lv <= spec.level ? CYAN : "#27272a", boxShadow: lv <= spec.level ? `0 0 6px ${CYAN}66` : "none" }} />
                        ))}
                      </div>
                    </div>
                    <button data-testid={`upgrade-${part}`} disabled={busy || spec.maxed} onClick={() => buyUpgrade(part)}
                      className="shrink-0 px-3.5 py-2 font-mono text-[9px] uppercase tracking-[0.15em] font-bold text-black disabled:opacity-35 transition-opacity"
                      style={{ backgroundColor: "#34d399" }}>
                      {spec.maxed ? "Maxed" : `Buy ${fmt$(spec.next_price)}`}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* live metrics */}
        <div className="lg:col-span-3 flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-white/10 bg-black/50 p-4">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Cpu className="w-3 h-3" /> Hashrate</p>
              <p data-testid="crypto-hashrate" className="font-black text-2xl mt-1" style={{ color: rig?.running ? CYAN : "#52525b" }}>{rig?.hashrate_mh ?? 0}<span className="text-xs font-mono text-zinc-500 ml-1">MH/s</span></p>
              {(rig?.throttle ?? 1) < 1 && <p className="font-mono text-[8px] text-amber-400 uppercase mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Thermal throttle {Math.round((rig?.throttle ?? 1) * 100)}%</p>}
            </div>
            <div className="border border-white/10 bg-black/50 p-4">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Thermometer className="w-3 h-3" /> Core temp</p>
              <p data-testid="crypto-temp" className="font-black text-2xl mt-1" style={{ color: tempColor }}>{temp.toFixed(1)}°C</p>
              <div className="h-1.5 bg-black/70 border border-white/10 mt-2"><div className="h-full transition-all duration-1000" style={{ width: `${Math.min(100, (temp - 30) / 0.55)}%`, backgroundColor: tempColor }} /></div>
            </div>
            <div className="border border-white/10 bg-black/50 p-4">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Zap className="w-3 h-3" /> Power</p>
              <p data-testid="crypto-watts" className="font-black text-2xl mt-1 text-zinc-200">{rig?.watts ?? 12}<span className="text-xs font-mono text-zinc-500 ml-1">W</span></p>
            </div>
          </div>

          {/* wallet buffer */}
          <div className="border p-5" style={{ borderColor: `${CYAN}33`, backgroundColor: "#00000066" }}>
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Wallet buffer — unsold</p>
            <div className="flex items-end gap-4 flex-wrap">
              <p data-testid="crypto-pending" className="font-black text-4xl leading-none" style={{ color: CYAN, textShadow: `0 0 24px ${CYAN}55` }}>
                {pending.toFixed(4)} <span className="text-base text-zinc-400">CIV</span>
              </p>
              <p data-testid="crypto-est" className="font-mono text-sm text-zinc-300 pb-0.5">≈ {fmt$(Math.round(est))} at current rate</p>
              <button data-testid="crypto-collect" disabled={busy || pending < 0.01} onClick={() => act("collect")}
                className="ml-auto px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-black disabled:opacity-40 flex items-center gap-2"
                style={{ backgroundColor: "#34d399", boxShadow: "0 0 24px #34d39933" }}>
                <ArrowDownToLine className="w-4 h-4" /> Sell all → bank
              </button>
            </div>
            <p className="font-mono text-[8px] text-zinc-600 mt-3 uppercase tracking-[0.15em]">Lifetime mined {rig?.total_mined_civ ?? 0} CIV · market rate drifts every poll — time your sell</p>
          </div>

          {/* rate sparkline */}
          <div className="border border-white/10 bg-black/50 p-4 flex-1 min-h-28">
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-2">CIV market — last {rateHist.length} polls</p>
            <svg data-testid="crypto-chart" viewBox="0 0 300 60" className="w-full h-16" preserveAspectRatio="none">
              <polyline fill="none" stroke={CYAN} strokeWidth="1.5"
                points={rateHist.map((r, i) => `${(i / Math.max(1, rateHist.length - 1)) * 300},${58 - ((r - minHist) / Math.max(1, maxHist - minHist)) * 54}`).join(" ")} />
            </svg>
          </div>

          {/* rig security log — sabotage alerts */}
          {alerts.length > 0 && (
            <div data-testid="rig-alerts" className="border border-red-500/40 bg-red-500/[0.06] p-4 flex flex-col gap-2">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-red-400 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Rig security log</p>
              {alerts.slice(0, 3).map((a) => (
                <p key={a.id} data-testid={`rig-alert-${a.id.slice(0, 8)}`} className="font-mono text-[9px] text-zinc-300 leading-relaxed">
                  <span className={a.kind === "sabotaged" ? "text-red-400" : "text-amber-400"}>{a.kind === "sabotaged" ? "HIT" : "ATTEMPT"}</span>
                  {" · "}{a.by} — {a.detail}
                </p>
              ))}
            </div>
          )}

          {/* rival rigs — sabotage targets */}
          <div data-testid="rival-rigs" className="border border-white/10 bg-black/50 p-4">
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-3 flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-red-400" /> Rival rigs — unattended & hashing</p>
            {rivals.length === 0 && <p data-testid="rivals-empty" className="font-mono text-[9px] text-zinc-600">No exposed rigs right now — rivals are offline, watching their rigs, or their buffers are empty.</p>}
            <div className="flex flex-col gap-2">
              {rivals.map((r) => (
                <div key={r.playercode} data-testid={`rival-${r.playercode}`} className="border border-white/10 bg-black/40 px-3.5 py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] text-zinc-200 truncate">{r.name}</p>
                    <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.1em]">{r.hashrate_mh} MH/s · buffer {r.pending_civ.toFixed(4)} CIV ≈ {fmt$(Math.round(r.est_value))}</p>
                  </div>
                  <button data-testid={`sabotage-${r.playercode}`} disabled={sabBusy === r.playercode} onClick={() => sabotage(r)}
                    className="shrink-0 px-3.5 py-2 font-mono text-[9px] uppercase tracking-[0.15em] font-bold border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> {sabBusy === r.playercode ? "Cutting…" : "Short-circuit"}
                  </button>
                </div>
              ))}
            </div>
            <p className="font-mono text-[8px] text-zinc-600 mt-3 uppercase tracking-[0.15em]">70% success · skim 40% of the buffer · botch it and you eat a $500 fine while the owner gets pinged</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
